import { getCellName } from 'components/atoms/ConditionBuilder/AttributesTableConditionBuilder';
import { isCell, isLogical, isOverlap, validateCondition } from 'components/atoms/ConditionBuilder/utils';
import { labelColumnName } from 'components/Procedure/useSlideChannelsAndResults/parseHeatmaps';
import {
  AutomaticCondition,
  CategoricalActions,
  conditionOperatorFilterAction,
  conditionOperatorFilterActionOptions,
  ConditionType,
  overlapVariablesBackend,
} from 'interfaces/automaticCondition';
import { OrchestrationData } from 'interfaces/calculateFeatures';
import PostProcessingAction, {
  Input,
  InputType,
  MappingFilterMetadata,
  PostProcessingActionCreated,
  PostProcessingActionInputCondition,
} from 'interfaces/postProcessingAction';
import {
  Dictionary,
  difference,
  every,
  find,
  first,
  flatMap,
  forEach,
  get,
  includes,
  indexOf,
  isArray,
  isBoolean,
  isEmpty,
  isNumber,
  keys,
  map,
  remove,
  set,
  size,
  some,
  toString,
  values,
} from 'lodash';
import { uuidv4 } from 'utils/helpers';
import { DEPENDENT_ON_INPUT_TYPE, NEIGHBOR_STAINS_INPUT_KEY, TARGET_STAINS_INPUT_KEY } from '../consts';
export const createNewAction = (
  postProcessingActionId: string,
  postProcessingActionsById: Dictionary<PostProcessingAction>
) => {
  const newAction = {
    id: uuidv4(),
    actionId: postProcessingActionId,
    actionName: postProcessingActionsById[postProcessingActionId].actionName,
    actionInput: postProcessingActionsById[postProcessingActionId].actionInput,
  };

  // add default values of inputs
  map(postProcessingActionsById[postProcessingActionId].inputs, (input) => {
    if (!isEmpty(input?.defaultValue)) {
      const defaultValue =
        input.inputType === 'number'
          ? Number(input.defaultValue)
          : input.inputType === 'boolean'
          ? Boolean(input.defaultValue)
          : input.defaultValue;
      set(newAction, input.inputKey, defaultValue);
    }
  });

  return newAction;
};

/**
 * Convert AutomaticCondition to backend format recursively
 * @param logicalQuery
 * @returns
 */
const convertLogicalQueryRecursive = (logicalQuery: AutomaticCondition): any => {
  let logicalQueryConverted: Record<string, any> = {};
  if (isLogical(logicalQuery)) {
    set(
      logicalQueryConverted,
      conditionOperatorFilterAction[logicalQuery.operator],
      map(logicalQuery.operands, (operand) => convertLogicalQueryRecursive(operand))
    );
  }

  if (isCell(logicalQuery)) {
    logicalQueryConverted[logicalQuery.mappingFilter] = {
      [logicalQuery.filterAction]: logicalQuery.toFilter,
    };
  }

  if (isOverlap(logicalQuery)) {
    logicalQueryConverted[overlapVariablesBackend[ConditionType.OVERLAP]] = {
      filters: convertLogicalQueryRecursive(first(logicalQuery.filters)),
      [overlapVariablesBackend['minFraction']]: logicalQuery.minFraction,
      [overlapVariablesBackend['maxFraction']]: logicalQuery.maxFraction,
    };
  }

  return logicalQueryConverted;
};

export const convertLogicalQueryToBackend = (logicalQuery: AutomaticCondition) => {
  return convertLogicalQueryRecursive(logicalQuery);
};

// Convert from backend
// For example:
// logicalQuery = { 'cell.label': { $in: ['cell1', 'cell2'] } };
// AttributesTableCondition = {
//   conditionType: 'AttributesTableCondition',
//   filterAction: '$in',
//   mappingFilter: 'cell.label',
//   toFilter: ['cell1', 'cell2'],
// };
// logicalQuery = { overlap: { filters:  { 'cell.label': { $in: ['cell1', 'cell2'] } } , minFraction: 0.5, maxFraction: 0.8 } };
// overlapCondition = {
//   conditionType: 'OverlapCondition',
//   filters: [cellCondition],
//   minFraction: 0.5,
//   maxFraction: 0.8
// };

export const convertLogicalQueryFromBackend = (
  logicalQuery: Record<string, any>,
  mappingFiltersMetadataForLogicalQuery: MappingFilterMetadata[]
): AutomaticCondition => {
  if (!logicalQuery) {
    console.warn('convertLogicalQueryFromBackend called with null/undefined parameters', {
      logicalQuery,
    });
    return null;
  }
  const mappingFilterOrActionOperator = first(keys(logicalQuery));

  if (includes(map(conditionOperatorFilterActionOptions, 'value'), mappingFilterOrActionOperator)) {
    return {
      operator: find(conditionOperatorFilterActionOptions, (option) => option.value === mappingFilterOrActionOperator)
        ?.label,
      operands: map(logicalQuery[mappingFilterOrActionOperator], (query) =>
        convertLogicalQueryFromBackend(query, mappingFiltersMetadataForLogicalQuery)
      ),
    };
  }

  if (mappingFilterOrActionOperator === overlapVariablesBackend[ConditionType.OVERLAP]) {
    const overlap = logicalQuery[overlapVariablesBackend[ConditionType.OVERLAP]];
    return {
      conditionType: ConditionType.OVERLAP,
      filters: [convertLogicalQueryFromBackend(overlap.filters, mappingFiltersMetadataForLogicalQuery)],
      minFraction: overlap[overlapVariablesBackend['minFraction']],
      maxFraction: overlap[overlapVariablesBackend['maxFraction']],
    };
  }

  const attributesTableCondition = {
    conditionType: ConditionType.ATTRIBUTES_TABLE,
    mappingFilter: mappingFilterOrActionOperator,
    filterAction: first(keys(logicalQuery[mappingFilterOrActionOperator])),
    toFilter: first(values(logicalQuery[mappingFilterOrActionOperator])),
  };

  return {
    ...attributesTableCondition,
    displayValue: getCellName(attributesTableCondition, mappingFiltersMetadataForLogicalQuery),
  };
};

export const checkOptionExist = (inputValue: any, input: Input, options: any[]) => {
  const optionIds = map(options, 'id');

  if (isEmpty(inputValue) || input?.freeSolo) {
    return true;
  }

  return input?.inputSource && isArray(inputValue)
    ? isEmpty(difference(inputValue, optionIds))
    : some(options, { id: inputValue });
};

export const isFeatureBetweenStains = (action: PostProcessingActionCreated) => {
  return includes(action.actionName, 'between_stains');
};

// For more complex features, we need to add specific validations
export const getSpecificInputError = (inputValue: any, input: Input, action: PostProcessingActionCreated) => {
  if (isFeatureBetweenStains(action) && input.inputKey === NEIGHBOR_STAINS_INPUT_KEY) {
    return getNeighborStainsError(inputValue, action[TARGET_STAINS_INPUT_KEY]);
  }
};

export const getNeighborStainsError = (inputValue: string[], selectedTargetStains: string[]) => {
  const differenceBetweenInputs = difference(inputValue, selectedTargetStains);
  if (!isEmpty(inputValue) && isEmpty(differenceBetweenInputs)) {
    return 'Must contain stain that is different from the target stains';
  }
};

export const getInputType = (input: Input, action: PostProcessingActionCreated) => {
  return input.inputType == DEPENDENT_ON_INPUT_TYPE ? action.dependentOnInputTypes?.[input.inputKey] : input.inputType;
};

export const validateInput = (
  inputValue: any,
  input: Input,
  action: PostProcessingActionCreated,
  options?: any[],
  categoricalActions?: CategoricalActions[]
) => {
  if (isEmpty(inputValue) && !getIsRequired(input.required, action)) {
    return true;
  }

  const inputType = getInputType(input, action);

  if (inputType === InputType.NUMBER) {
    return isNumber(inputValue);
  }

  if (inputType === InputType.NUMBERS) {
    return isArray(inputValue) && every(inputValue, isNumber);
  }

  if (inputType === InputType.BOOLEAN) {
    return isBoolean(inputValue);
  }

  if (inputType === InputType.LOGICAL_QUERY && !validateCondition(inputValue, options, categoricalActions)[0]) {
    return false;
  }

  if (
    (inputType === InputType.SELECT || inputType === InputType.MULTISELECT) &&
    !checkOptionExist(inputValue, input, options)
  ) {
    return false;
  }

  if (getSpecificInputError(inputValue, input, action)) {
    return false;
  }

  if (inputType === InputType.RADIO && !checkOptionExist(inputValue, input, options)) {
    return false;
  }

  const isRequired = getIsRequired(input.required, action);

  if (isRequired && isEmpty(inputValue)) {
    return false;
  }

  return true;
};

export const getConditionValue = (
  inputCondition: PostProcessingActionInputCondition,
  action: PostProcessingActionCreated
) => {
  if (inputCondition.action === 'equals') {
    return get(action, inputCondition.key) === inputCondition.value;
  } else if (inputCondition.action === 'not equals') {
    return get(action, inputCondition.key) !== inputCondition.value;
  } else if (inputCondition.action === 'subset') {
    return includes(get(action, inputCondition.key), inputCondition.value);
  }
};

export const getIsRequired = (
  required: PostProcessingActionInputCondition | boolean,
  action: PostProcessingActionCreated
) => {
  return isBoolean(required) ? required : getConditionValue(required, action);
};

// For example:
// labels =  [['a', 'b'], ['c', 'd']]
// return ['a.c', 'a.d, 'b.c', 'b.d']
export const combineArrays = (labels: string[][]): string[] => {
  // Base case: if only one array is left, return it as is
  if (labels.length === 1) return first(labels);

  // Combine the first array with the result of combining the rest recursively
  const [firstLabels, ...rest] = labels;
  const combinedRest = combineArrays(rest);

  // Use flatMap to concatenate elements from the first array with the combined rest
  return flatMap(firstLabels, (prefix) => map(combinedRest, (suffix) => `${prefix}.${suffix}`));
};

export const hasNonEmptyNewOptionValues = (newOptionValues: string[]): boolean => {
  return some(newOptionValues, (value) => !isEmpty(value));
};

export const getActionsWithCorrectNewOptionValueAfterIndexChange = (
  newActions: PostProcessingActionCreated[],
  oldIndex: number,
  newIndex: number,
  postProcessingActionsById: Dictionary<PostProcessingAction>,
  getOptionsByValue: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => Dictionary<{ id: string | number; label: string }>
) => {
  const correctNewActions = [...newActions];

  if (newIndex > oldIndex) {
    if (hasNonEmptyNewOptionValues(correctNewActions[oldIndex].newOptionValues)) {
      // check if actions that not suppose to know this newOptionValues used it
      const newOptions = correctNewActions[oldIndex].newOptionValues;
      const isActionWithMultipleNewOptions = size(newOptions) > 1;
      for (let index = oldIndex + 1; index <= newIndex; index++) {
        const action = correctNewActions[index];
        const postProcessingAction = postProcessingActionsById[action.actionId];
        if (
          action.stain == correctNewActions[oldIndex].stain &&
          action.actionInput == correctNewActions[oldIndex].actionInput
        ) {
          for (let input of postProcessingAction.inputs) {
            if (input.freeSolo) {
              const indexOfNewOption = indexOf(newOptions, get(action, input.inputKey));
              if (indexOfNewOption !== -1) {
                // if freesolo and the new option value is used in the action - add to newOptionValues and delete from old action
                correctNewActions[index].newOptionValues = [newOptions[indexOfNewOption]];
                if (isActionWithMultipleNewOptions) {
                  // change to null and not remove
                  newOptions[indexOfNewOption] = null;
                } else {
                  remove(newOptions, (value) => value === newOptions[indexOfNewOption]);
                }
              }
            }

            if (!isEmpty(input.children)) {
              // if the input has children, we need to check them too
              forEach(input.children, (childInput) => {
                if (childInput.freeSolo) {
                  forEach(action[input.inputKey], (childValue, childIdx) => {
                    const childIndexOfNewOption = indexOf(newOptions, get(childValue, childInput.inputKey));
                    if (childIndexOfNewOption !== -1) {
                      // if freesolo and the new option value is used in the action - add to newOptionValues and delete from old action
                      correctNewActions[index].newOptionValues[Number(childIdx)] = newOptions[childIndexOfNewOption];
                      if (isActionWithMultipleNewOptions) {
                        newOptions[childIndexOfNewOption] = null;
                      } else {
                        remove(newOptions, (value) => value === newOptions[childIndexOfNewOption]);
                      }
                    }
                  });
                }
              });
            }
          }

          // all the other actions that used this newOptionValues can continue to use it
          if (
            size(newOptions) === 0 ||
            (isActionWithMultipleNewOptions && every(newOptions, (value) => value === null))
          ) {
            break;
          }
        }
      }
      correctNewActions[oldIndex].newOptionValues = newOptions;
    }
  } else {
    const postProcessingAction = postProcessingActionsById[correctNewActions[oldIndex].actionId];

    for (let input of postProcessingAction.inputs) {
      if (input.freeSolo) {
        // if freesolo - add to newOptionValues and delete from old action
        const options = getOptionsByValue(
          input.inputSource,
          correctNewActions[oldIndex].stain,
          newIndex,
          correctNewActions[oldIndex].actionInput
        );
        const inputValue = get(correctNewActions[oldIndex], input.inputKey);
        // if the value is not exist anymore in the options
        if (!isEmpty(options) && !options[inputValue] && !isEmpty(inputValue)) {
          correctNewActions[oldIndex].newOptionValues = [inputValue];
          // search for the action that used this newOptionValues and delete it
          for (let index = newIndex; index < oldIndex; index++) {
            const action = correctNewActions[index];
            const indexOfNewOption = indexOf(action.newOptionValues, inputValue);

            if (
              action.stain == correctNewActions[oldIndex].stain &&
              action.actionInput == correctNewActions[oldIndex].actionInput &&
              indexOfNewOption !== -1
            ) {
              if (size(action.newOptionValues) > 1) {
                correctNewActions[index].newOptionValues[indexOfNewOption] = null;
              } else {
                correctNewActions[index].newOptionValues = [];
              }
              break;
            }
          }
        }
      }

      if (!isEmpty(input.children)) {
        // if the input has children, we need to check them too
        forEach(input.children, (childInput) => {
          if (childInput.freeSolo) {
            forEach(correctNewActions[oldIndex][input.inputKey], (childValue, childIdx) => {
              const options = getOptionsByValue(
                childInput.inputSource,
                correctNewActions[oldIndex].stain,
                newIndex,
                correctNewActions[oldIndex].actionInput
              );
              const childValueKey = get(childValue, childInput.inputKey);
              // if the value is not exist anymore in the options
              if (!isEmpty(options) && !options[childValueKey] && !isEmpty(childValueKey)) {
                correctNewActions[oldIndex].newOptionValues[Number(childIdx)] = childValueKey;
                // search for the action that used this newOptionValues and delete it
                for (let index = newIndex; index < oldIndex; index++) {
                  const action = correctNewActions[index];
                  const indexOfNewOption = indexOf(action.newOptionValues, childValueKey);

                  if (
                    action.stain == correctNewActions[oldIndex].stain &&
                    action.actionInput == correctNewActions[oldIndex].actionInput &&
                    indexOfNewOption !== -1
                  ) {
                    if (size(action.newOptionValues) > 1) {
                      correctNewActions[index].newOptionValues[indexOfNewOption] = null;
                    } else {
                      correctNewActions[index].newOptionValues = [];
                    }
                    break;
                  }
                }
              }
            });
          }
        });
      }
    }
  }

  return correctNewActions;
};

/**
 * Get detection label options from slide_detection_table orchestration data
 * @param orchestrationData orchestration data from slide_detection_table
 * @returns array of detection label options (id and label)
 */
export const getDetectionLabelOptions = (orchestrationData: OrchestrationData[]): { id: string; label: string }[] => {
  if (!isArray(orchestrationData)) {
    return [];
  }

  const labelField = find(orchestrationData, { label: labelColumnName });

  return isArray(labelField?.options)
    ? map(labelField.options, (label) => ({ id: toString(label), label: toString(label) }))
    : [];
};
